"use client";

import { c as _c } from "react/compiler-runtime";
import { useEmoji } from "@/contexts/EmojiContext";
import { getReactionSnapshot, getReactionSnapshotServer, getTopReactions, subscribeToReactionStore } from "@/helpers/reactions/reactionHistory";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropReaction } from "@/hooks/drops/useDropReaction";
import Image from "next/image";
import React, { useCallback, useMemo, useSyncExternalStore } from "react";
import { Tooltip } from "react-tooltip";
const MAX_QUICK_REACTIONS = 3;
const WaveDropActionsQuickReact: React.FC<{
  readonly drop: ExtendedDrop;
  readonly isMobile?: boolean;
  readonly onReacted?: () => void;
}> = t0 => {
  const $ = _c(10);
  const {
    drop,
    isMobile: t1,
    onReacted
  } = t0;
  const isMobile = t1 === undefined ? false : t1;
  const {
    react,
    canReact
  } = useDropReaction(drop, onReacted);
  const reactionSnapshot = useSyncExternalStore(subscribeToReactionStore, getReactionSnapshot, getReactionSnapshotServer);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getTopReactions(MAX_QUICK_REACTIONS);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const topReactionCodes = t2;
  let t3;
  if ($[1] !== canReact || $[2] !== drop || $[3] !== isMobile || $[4] !== react) {
    t3 = topReactionCodes.map(code => <QuickReactButton key={code} reactionCode={code} dropId={drop.id} canReact={canReact} onReact={react} isMobile={isMobile} />);
    $[1] = canReact;
    $[2] = drop;
    $[3] = isMobile;
    $[4] = react;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const buttons = t3;
  if (isMobile) {
    let t4;
    if ($[6] !== buttons) {
      t4 = <div className="tw-flex tw-items-center tw-justify-start tw-gap-x-2 tw-rounded-xl tw-bg-iron-950 tw-p-3">{buttons}</div>;
      $[6] = buttons;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t4;
  if ($[8] !== buttons) {
    t4 = <>{buttons}</>;
    $[8] = buttons;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const QuickReactButton: React.FC<{
  readonly reactionCode: string;
  readonly dropId: string;
  readonly canReact: boolean;
  readonly onReact: (code: string) => void;
  readonly isMobile?: boolean;
}> = t0 => {
  const $ = _c(47);
  const {
    reactionCode,
    dropId,
    canReact,
    onReact,
    isMobile: t1
  } = t0;
  const isMobile = t1 === undefined ? false : t1;
  const {
    emojiMap,
    findNativeEmoji
  } = useEmoji();
  let t2;
  if ($[0] !== reactionCode) {
    t2 = reactionCode.replaceAll(":", "");
    $[0] = reactionCode;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const emojiId = t2;
  const emojiSize = isMobile ? "tw-size-7" : "tw-size-5";
  const textSize = isMobile ? "tw-text-[1.625rem]" : "tw-text-[1.25rem]";
  let t3;
  bb0: {
    const t4 = `tw-flex tw-items-center tw-justify-center ${textSize} tw-leading-none`;
    let t5;
    if ($[2] !== t4) {
      t5 = <span className={t4}>👍</span>;
      $[2] = t4;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    const fallback = t5;
    if (!emojiMap.length) {
      t3 = fallback;
      break bb0;
    }
    let t6;
    if ($[4] !== emojiId || $[5] !== emojiMap) {
      let t7;
      if ($[7] !== emojiId) {
        t7 = e => e.id === emojiId;
        $[7] = emojiId;
        $[8] = t7;
      } else {
        t7 = $[8];
      }
      t6 = emojiMap.flatMap(_temp).find(t7);
      $[4] = emojiId;
      $[5] = emojiMap;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    const custom = t6;
    const customSrc = custom?.skins[0]?.src;
    if (customSrc) {
      const t7 = `tw-relative ${emojiSize}`;
      let t8;
      if ($[9] !== customSrc || $[10] !== emojiId) {
        t8 = <Image src={customSrc} alt={emojiId} fill={true} className="tw-object-contain" />;
        $[9] = customSrc;
        $[10] = emojiId;
        $[11] = t8;
      } else {
        t8 = $[11];
      }
      let t9;
      if ($[12] !== t7 || $[13] !== t8) {
        t9 = <div className={t7}>{t8}</div>;
        $[12] = t7;
        $[13] = t8;
        $[14] = t9;
      } else {
        t9 = $[14];
      }
      t3 = t9;
      break bb0;
    }
    let t7;
    if ($[15] !== emojiId || $[16] !== findNativeEmoji) {
      t7 = findNativeEmoji(emojiId);
      $[15] = emojiId;
      $[16] = findNativeEmoji;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    const native = t7;
    if (native?.skins[0]?.native) {
      const t8 = `tw-flex tw-items-center tw-justify-center ${textSize} tw-leading-none`;
      let t9;
      if ($[18] !== native.skins[0].native || $[19] !== t8) {
        t9 = <span className={t8}>{native.skins[0].native}</span>;
        $[18] = native.skins[0].native;
        $[19] = t8;
        $[20] = t9;
      } else {
        t9 = $[20];
      }
      t3 = t9;
      break bb0;
    }
    t3 = fallback;
  }
  const emojiNode = t3;
  let t4;
  if ($[21] !== onReact || $[22] !== reactionCode) {
    t4 = () => {
      onReact(reactionCode);
    };
    $[21] = onReact;
    $[22] = reactionCode;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  const handleClick = t4;
  const tooltipId = `quick-react-${dropId}-${emojiId}`;
  if (isMobile) {
    const t5 = `tw-flex tw-size-11 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-iron-800 tw-transition-colors tw-duration-200 ${canReact ? "active:tw-bg-iron-700" : "tw-cursor-default tw-opacity-50"}`;
    const t6 = !canReact;
    let t7;
    if ($[24] !== emojiNode || $[25] !== handleClick || $[26] !== t5 || $[27] !== t6) {
      t7 = <button className={t5} onClick={handleClick} disabled={t6} aria-label="Click to react">{emojiNode}</button>;
      $[24] = emojiNode;
      $[25] = handleClick;
      $[26] = t5;
      $[27] = t6;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    return t7;
  }
  const t5 = `tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-iron-400 tw-transition-all tw-duration-200 tw-ease-out desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-[#FFCC22] ${canReact ? "tw-cursor-pointer" : "tw-cursor-default tw-opacity-50"}`;
  const t6 = !canReact;
  let t7;
  if ($[29] !== canReact || $[30] !== tooltipId) {
    t7 = canReact ? {
      "data-tooltip-id": tooltipId
    } : {};
    $[29] = canReact;
    $[30] = tooltipId;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  const t8 = `tw-flex tw-size-5 tw-flex-shrink-0 tw-items-center tw-justify-center tw-transition tw-duration-300 tw-ease-out ${canReact ? "" : "tw-opacity-50"}`;
  let t9;
  if ($[32] !== emojiNode || $[33] !== t8) {
    t9 = <div className={t8}>{emojiNode}</div>;
    $[32] = emojiNode;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== handleClick || $[36] !== t5 || $[37] !== t6 || $[38] !== t7 || $[39] !== t9) {
    t10 = <button className={t5} onClick={handleClick} disabled={t6} aria-label="Click to react" {...t7}>{t9}</button>;
    $[35] = handleClick;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== canReact || $[42] !== tooltipId) {
    t11 = canReact && <Tooltip id={tooltipId} place="top" positionStrategy="fixed" offset={8} opacity={1} style={TOOLTIP_STYLES}><span className="tw-text-xs">Click to react</span></Tooltip>;
    $[41] = canReact;
    $[42] = tooltipId;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== t10 || $[45] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[44] = t10;
    $[45] = t11;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  return t12;
};
export default WaveDropActionsQuickReact;
function _temp(cat) {
  return cat.emojis;
}